import { useCallback, useState } from 'react';

import { getSoil } from '@/endpoints';
import { SchoolcraftSoilResponse } from '@/endpoints/schoolcraft/soil/getSoil';
import Logger from '@/utils/logger';
import {
  getSoilAverages,
  guessActiveKey,
  ISoilAverages,
  SoilIndex,
} from '@/utils/uniqueEndpoints';

export interface ISoilData {
  activeSoilScoreIndex: SoilIndex | undefined;
  soilAverages: ISoilAverages | undefined;
  soilData: SchoolcraftSoilResponse | null;
}

export function useEmbedSoil(boundary) {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [soilData, setSoilData] = useState<ISoilData>({
    activeSoilScoreIndex: undefined,
    soilAverages: undefined,
    soilData: null,
  });

  const fetchSoilData = useCallback(async () => {
    if (!boundary) {
      return;
    }

    try {
      setLoading(true);
      const { res, err } = await getSoil({
        boundary,
      });

      if (err) {
        setError(err);
        throw Error(err.message);
      }

      const soilResData = res?.data;

      if (!soilResData) {
        setSoilData({
          activeSoilScoreIndex: undefined,
          soilAverages: undefined,
          soilData: null,
        });
      } else {
        const average = getSoilAverages(soilResData, [
          {
            geometry: boundary.geometry,
            id: 0,
            properties: {},
            type: 'Feature',
          },
        ]);
        const index = guessActiveKey(soilResData);

        setSoilData({
          activeSoilScoreIndex: index,
          soilAverages: average,
          soilData: soilResData,
        });
      }
    } catch (err) {
      Logger.error(err);
    } finally {
      setLoading(false);
    }
  }, [boundary]);

  return {
    error,
    fetchData: fetchSoilData,
    loading,
    soilData,
  };
}
